import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, fileUrl } from "@/lib/api";
import { Bell, Heart, MessageCircle, Eye, BadgeCheck, X } from "lucide-react";

const TYPE_META = {
  interest_received: { icon: Heart,        label: "New interest",  copy: (n) => `${n.from_user?.name || "Someone"} sent you an interest`, target: () => "/dashboard" },
  interest_accepted: { icon: BadgeCheck,   label: "Match accepted", copy: (n) => `${n.from_user?.name || "Someone"} accepted your interest`, target: (n) => `/profile/${n.from_user_id}` },
  interest_rejected: { icon: X,            label: "Declined",       copy: (n) => `${n.from_user?.name || "Someone"} declined your interest`, target: () => "/dashboard" },
  new_message:       { icon: MessageCircle, label: "Message",       copy: (n) => `${n.from_user?.name || "Someone"} sent you a message`, target: (n) => `/messages?to=${n.from_user_id}` },
  profile_view:      { icon: Eye,          label: "Profile view",   copy: (n) => `${n.from_user?.name || "Someone"} viewed your profile`, target: (n) => `/profile/${n.from_user_id}` },
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setItems(data.results || []);
        if ((data.results || []).some((n) => !n.read)) {
          await api.post("/notifications/read-all");
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const onClick = (n) => {
    const meta = TYPE_META[n.type];
    if (meta) navigate(meta.target(n));
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="notifications-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <p className="overline">All activity</p>
        <h1 className="font-serif text-4xl text-brand-ink mt-2">Notifications</h1>

        {loading ? (
          <div className="mt-10 space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-white border border-brand-line rounded-md animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center bg-white border border-brand-line rounded-lg p-12">
            <Bell className="h-10 w-10 text-brand-inkMute mx-auto" strokeWidth={1.2} />
            <p className="font-serif text-2xl text-brand-ink mt-4">All caught up</p>
            <p className="text-sm text-brand-inkLight mt-2">When someone sends you an interest, accepts yours, messages you, or views your profile, you&apos;ll see it here.</p>
          </div>
        ) : (
          <ul className="mt-8 bg-white border border-brand-line rounded-lg divide-y divide-brand-line overflow-hidden" data-testid="notifications-list">
            {items.map((n) => {
              const meta = TYPE_META[n.type] || { icon: Bell, label: n.type, copy: () => n.type, target: () => "/dashboard" };
              const Icon = meta.icon;
              return (
                <li key={n.id}>
                  <button onClick={() => onClick(n)} className="w-full text-left px-5 py-4 hover:bg-brand-muted/60 transition-colors flex items-center gap-4" data-testid={`notif-row-${n.id}`}>
                    {n.from_user?.photo_url ? (
                      <img src={fileUrl(n.from_user.photo_url)} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <span className="w-12 h-12 rounded-full bg-brand-muted text-brand-maroon flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-brand-gold">{meta.label}</p>
                      <p className="text-sm text-brand-ink mt-0.5">{meta.copy(n)}</p>
                      <p className="text-[11px] text-brand-inkMute mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
